/**
 * Product Video Utilities — Web Client
 * ─────────────────────────────────────────────────────────────────────
 * Helpers for the frozen product-video contract shared with the
 * Dashboard server (utils/productVideos.js there). Each video entry:
 *
 *   { kind: "CLOUDINARY" | "YOUTUBE",
 *     url: String,          // Cloudinary DELIVERY url (H.264 mp4, see
 *                           //   playableCloudinaryVideoUrl) OR YouTube watch URL
 *     publicId: String,     // CLOUDINARY only; "" for YOUTUBE
 *     thumbnailUrl: String, // poster frame / hqdefault.jpg
 *     durationSec: Number } // 0 when unknown
 *
 * Max 3 videos per product. The server sanitizes on write; these
 * helpers re-validate defensively on read so a malformed record can
 * never break a product page.
 */

/**
 * Extract the 11-char YouTube video id from any of the URL shapes
 * vendors paste: watch?v=, youtu.be/, shorts/, embed/, live/.
 * Returns null when no id can be found.
 */
export const getYouTubeId = (url) => {
    if (typeof url !== "string" || !url) return null;
    const match = url.match(
        /(?:youtube\.com\/(?:watch\?(?:[^#]*&)?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{6,20})/
    );
    return match ? match[1] : null;
};

/**
 * Build a privacy-sane embed URL for the inline player. Autoplay is on
 * by default because the player only mounts after an explicit tap on a
 * video thumbnail.
 */
export const buildYouTubeEmbedUrl = (url, { autoplay = true } = {}) => {
    const id = getYouTubeId(url);
    if (!id) return null;
    const params = autoplay
        ? "autoplay=1&playsinline=1&rel=0"
        : "playsinline=1&rel=0";
    return `https://www.youtube.com/embed/${id}?${params}`;
};

/* ── Cloudinary delivery ──────────────────────────────────────────────
   Cloudinary serves whatever container and codec was UPLOADED unless the
   url asks otherwise. A .mov from an iPhone (QuickTime, usually HEVC)
   plays in Safari and is a silent black box in Chrome — the "it uploaded
   fine but does not play" report. The server now stores an H.264 mp4
   delivery url; this read-side mirror makes rows stored BEFORE that rule
   play too. Idempotent; non-Cloudinary and already-transformed urls are
   left alone. MIRROR of the server's utils/productVideos.js. */
const CLOUDINARY_VIDEO_DELIVERY = "q_auto,vc_auto";
const CLOUDINARY_UPLOAD_MARKER = "/video/upload/";

export const playableCloudinaryVideoUrl = (url) => {
    if (typeof url !== "string") return url;
    if (!/^https:\/\/res\.cloudinary\.com\//i.test(url)) return url;
    const at = url.indexOf(CLOUDINARY_UPLOAD_MARKER);
    if (at < 0) return url;
    const head = url.slice(0, at + CLOUDINARY_UPLOAD_MARKER.length);
    let rest = url.slice(at + CLOUDINARY_UPLOAD_MARKER.length);
    const firstSegment = rest.split("/")[0] || "";
    const isVersion = /^v\d+$/.test(firstSegment);
    const hasTransformation = !isVersion && /^[a-z]{1,3}_/i.test(firstSegment);
    if (!hasTransformation) rest = `${CLOUDINARY_VIDEO_DELIVERY}/${rest}`;
    const m = rest.match(/^([^?#]*)([?#].*)?$/);
    let path = m ? m[1] : rest;
    const suffix = m && m[2] ? m[2] : "";
    path = /\.[a-z0-9]{2,5}$/i.test(path)
        ? path.replace(/\.[a-z0-9]{2,5}$/i, ".mp4")
        : `${path}.mp4`;
    return `${head}${path}${suffix}`;
};

/**
 * Defensive read-side normalization of a product's `videos` array.
 * Drops entries that violate the contract, derives a missing YouTube
 * thumbnail, and clamps to the 3-video cap. Always returns an array.
 */
export const normalizeProductVideos = (videos) => {
    if (!Array.isArray(videos)) return [];
    const out = [];
    for (const v of videos) {
        if (!v || typeof v !== "object") continue;
        if (v.kind !== "CLOUDINARY" && v.kind !== "YOUTUBE") continue;
        if (typeof v.url !== "string" || !/^https:\/\//i.test(v.url)) continue;

        let thumbnailUrl =
            typeof v.thumbnailUrl === "string" ? v.thumbnailUrl : "";
        if (v.kind === "YOUTUBE") {
            const id = getYouTubeId(v.url);
            if (!id) continue;
            if (!thumbnailUrl) {
                thumbnailUrl = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
            }
        }

        out.push({
            kind: v.kind,
            url: v.kind === "CLOUDINARY" ? playableCloudinaryVideoUrl(v.url) : v.url,
            publicId: typeof v.publicId === "string" ? v.publicId : "",
            thumbnailUrl,
            durationSec:
                Number.isFinite(v.durationSec) && v.durationSec > 0
                    ? v.durationSec
                    : 0,
        });
        if (out.length >= 3) break;
    }
    return out;
};

/**
 * Format a duration in seconds as m:ss for thumbnail badges.
 * Returns "" for zero/unknown durations so callers can skip the badge.
 */
export const formatVideoDuration = (durationSec) => {
    if (!Number.isFinite(durationSec) || durationSec <= 0) return "";
    const total = Math.round(durationSec);
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
};
